/**
 * MusicView - Radio streams and music player with Spotify/Apple Music placeholders
 * 
 * Features:
 * - Tab 1: Radio (fully functional with German + Turkish stations)
 * - Tab 2: Spotify (placeholder UI, ready for OAuth integration)
 * - Tab 3: Apple Music (placeholder UI, ready for OAuth integration)
 * 
 * REFACTORED: Now uses global AudioPlayerContext for persistent playback
 */
import React, { useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Radio as RadioIcon, Music as MusicIcon, Loader, AlertCircle } from 'lucide-react';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { toast } from 'react-hot-toast';

// Import radio data from JSON
import radioData from '../../data/radios.json';

// Dummy data for Spotify placeholder
const SPOTIFY_PLAYLISTS = [
  { id: '1', name: 'Deutsche Hits', tracks: 50, icon: '🎵' },
  { id: '2', name: 'Entspannungsmusik', tracks: 30, icon: '🧘' },
  { id: '3', name: 'Schlager Classics', tracks: 40, icon: '🎤' }
];

// Dummy data for Apple Music placeholder
const APPLE_PLAYLISTS = [
  { id: '1', name: 'Top Charts', tracks: 100, icon: '📈' },
  { id: '2', name: 'Klassische Musik', tracks: 25, icon: '🎻' },
  { id: '3', name: 'Jazz & Blues', tracks: 35, icon: '🎺' }
];

export default function MusicView({ onBack }) {
  const {
    isPlaying,
    isLoading,
    currentStation,
    volume,
    error,
    play,
    pause,
    stop,
    changeVolume
  } = useAudioPlayer();

  const [activeTab, setActiveTab] = useState('radio'); // 'radio', 'spotify', 'apple'
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Load radio categories from JSON
  const radioCategories = radioData?.categories || [];

  // Load last selected category from localStorage on mount
  useEffect(() => {
    const savedCategoryId = localStorage.getItem('hayat_radio_category');
    if (savedCategoryId) {
      const savedCategory = radioCategories.find(cat => cat.id === savedCategoryId);
      if (savedCategory) {
        setSelectedCategory(savedCategory);
      }
    }
  }, []);

  // Save selected category to localStorage whenever it changes
  useEffect(() => {
    if (selectedCategory) {
      localStorage.setItem('hayat_radio_category', selectedCategory.id);
    } else {
      localStorage.removeItem('hayat_radio_category');
    }
  }, [selectedCategory]);

  const handleStationClick = (station) => {
    if (currentStation?.id === station.id && isPlaying) {
      pause();
    } else {
      // Add categoryId and categoryStations for next/previous functionality
      const stationWithCategory = { 
        ...station, 
        categoryId: selectedCategory?.id,
        categoryStations: selectedCategory?.stations || []
      };
      play(stationWithCategory);
      toast.success(`Spiele ${station.name}`);
    }
  };

  const handleVolumeChange = (e) => {
    changeVolume(parseFloat(e.target.value));
  };

  const handleStop = () => {
    stop();
    toast('Radio gestoppt');
  };

  // Handle Spotify connection (placeholder)
  const handleSpotifyConnect = () => {
    console.log('[Music] Spotify connection clicked (placeholder)');
    toast('Spotify-Integration wird in einer späteren Version verfügbar sein', {
      icon: '🎵',
      duration: 3000
    });
  };

  // Handle Apple Music connection (placeholder)
  const handleAppleMusicConnect = () => {
    console.log('[Music] Apple Music connection clicked (placeholder)');
    toast('Apple Music-Integration wird in einer späteren Version verfügbar sein', {
      icon: '🎶',
      duration: 3000
    });
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Musik & Radio
        </h1>
        <p className="text-xl text-gray-600">
          {activeTab === 'radio' && 'Hören Sie Ihre Lieblingssender'}
          {activeTab === 'spotify' && 'Spotify Musik-Streaming'}
          {activeTab === 'apple' && 'Apple Music Streaming'}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-3xl shadow-xl p-3 mb-6">
        <div className="grid grid-cols-3 gap-3">
          {/* Radio Tab */}
          <button
            onClick={() => setActiveTab('radio')}
            className={`py-4 px-6 rounded-2xl text-xl font-bold transition-all ${
              activeTab === 'radio'
                ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <RadioIcon className={`w-8 h-8 mx-auto mb-2 ${
              activeTab === 'radio' ? 'text-white' : 'text-pink-600'
            }`} />
            Radio
          </button>

          {/* Spotify Tab */}
          <button
            onClick={() => setActiveTab('spotify')}
            className={`py-4 px-6 rounded-2xl text-xl font-bold transition-all ${
              activeTab === 'spotify'
                ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="text-4xl mb-1">🎵</div>
            Spotify
          </button>

          {/* Apple Music Tab */}
          <button
            onClick={() => setActiveTab('apple')}
            className={`py-4 px-6 rounded-2xl text-xl font-bold transition-all ${
              activeTab === 'apple'
                ? 'bg-gradient-to-br from-red-500 to-pink-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="text-4xl mb-1">🎶</div>
            Apple Music
          </button>
        </div>
      </div>

      {/* ===== RADIO TAB ===== */}
      {activeTab === 'radio' && (
        <>
          {/* Category Selection */}
          {!selectedCategory && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Wähle eine Kategorie</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {radioCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category)}
                    className="p-6 bg-gradient-to-br from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100 rounded-3xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <div className="text-5xl mb-3">{category.icon}</div>
                    <div className="text-xl font-bold text-gray-900">{category.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {category.stations.length} Sender
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Station List for Selected Category */}
          {selectedCategory && (
            <div>
              {/* Back to Categories Button */}
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  if (isPlaying) pause();
                }}
                className="mb-6 flex items-center gap-2 text-purple-600 hover:text-purple-700 font-bold text-xl"
              >
                <span>←</span> Zurück zu Kategorien
              </button>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {selectedCategory.icon} {selectedCategory.name}
              </h2>
              <p className="text-gray-600 mb-6">{selectedCategory.stations.length} verfügbare Sender</p>
          
              {/* Now Playing */}
              {currentStation && (
                <div className="bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl shadow-2xl p-8 mb-6 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm opacity-80 mb-2">Jetzt läuft</div>
                      <h2 className="text-4xl font-bold mb-2 flex items-center gap-3">
                        {currentStation.logo} {currentStation.name}
                      </h2>
                      <div className="text-xl opacity-90">{currentStation.genre}</div>
                      
                      {/* Error Message */}
                      {error && (
                        <div className="mt-3 flex items-center gap-2 bg-red-500/30 backdrop-blur-sm px-4 py-2 rounded-lg">
                          <AlertCircle className="w-5 h-5" />
                          <span className="text-sm">{error}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {isLoading && (
                        <Loader className="w-12 h-12 animate-spin" />
                      )}
                      
                      {/* Play/Pause Button */}
                      <button
                        onClick={() => isPlaying ? pause() : play(currentStation)}
                        disabled={isLoading}
                        className="w-20 h-20 bg-white text-pink-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform disabled:opacity-50"
                      >
                        {isPlaying ? (
                          <Pause className="w-10 h-10" />
                        ) : (
                          <Play className="w-10 h-10 ml-1" />
                        )}
                      </button>
                      
                      {/* Stop Button */}
                      <button
                        onClick={handleStop}
                        className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl font-bold transition-all"
                      >
                        Stop
                      </button>
                    </div>
                  </div>

                  {/* Volume Control */}
                  <div className="mt-6 flex items-center gap-4">
                    <VolumeX className="w-6 h-6" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="flex-1 h-3 bg-white bg-opacity-30 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, white ${volume * 100}%, rgba(255,255,255,0.3) ${volume * 100}%)`
                      }}
                    />
                    <Volume2 className="w-6 h-6" />
                    <span className="text-lg font-bold w-12 text-right">
                      {Math.round(volume * 100)}%
                    </span>
                  </div>
                </div>
              )}

      {/* Radio Stations */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <RadioIcon className="w-8 h-8 text-pink-600" />
          Radio-Sender
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {selectedCategory?.stations.map((station) => {
            const isCurrentStation = currentStation?.id === station.id;
            const isThisPlaying = isCurrentStation && isPlaying;
            
            return (
              <button
                key={station.id}
                onClick={() => handleStationClick(station)}
                disabled={isLoading && isCurrentStation}
                className={`p-6 rounded-2xl transition-all text-left disabled:opacity-50 ${
                  isCurrentStation
                    ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-xl scale-105'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100 hover:from-pink-50 hover:to-purple-50 hover:scale-102'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">{station.logo}</span>
                      <h3 className="text-2xl font-bold">{station.name}</h3>
                    </div>
                    <p className={`text-lg ${
                      isCurrentStation ? 'opacity-90' : 'text-gray-600'
                    }`}>
                      {station.genre}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {isLoading && isCurrentStation ? (
                      <Loader className="w-10 h-10 animate-spin" />
                    ) : isThisPlaying ? (
                      <div className="w-10 h-10 flex items-center justify-center">
                        <div className="flex gap-1">
                          <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '20px', animationDelay: '0ms' }} />
                          <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '30px', animationDelay: '150ms' }} />
                          <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '25px', animationDelay: '300ms' }} />
                        </div>
                      </div>
                    ) : (
                      <Play className={`w-10 h-10 ${
                        isCurrentStation ? '' : 'text-pink-600'
                      }`} />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
            </div>
          )}
        </>
      )}

      {/* ===== SPOTIFY TAB (PLACEHOLDER) ===== */}
      {activeTab === 'spotify' && (
        <div className="space-y-6">
          {/* Info Card */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl shadow-xl p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-5xl">🎵</div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Spotify Integration</h2>
                <p className="text-lg text-gray-600">Musik-Streaming von Spotify</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 mb-6">
              <p className="text-xl text-gray-700 leading-relaxed">
                Die Spotify-Integration wird vorbereitet. Hier können später Ihre persönlichen Playlists, 
                Lieblingssongs und Empfehlungen angezeigt werden.
              </p>
            </div>

            {/* Connect Button (Disabled Placeholder) */}
            <button
              onClick={handleSpotifyConnect}
              className="w-full py-6 px-8 bg-gray-300 text-gray-600 rounded-2xl text-2xl font-bold cursor-not-allowed opacity-60 flex items-center justify-center gap-3"
              disabled
            >
              <div className="text-3xl">🔗</div>
              Mit Spotify verbinden (Demnächst)
            </button>
          </div>

          {/* Example Playlists */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Beispiel-Playlists</h3>
            <p className="text-lg text-gray-600 mb-6">
              So könnten Ihre Playlists später aussehen:
            </p>
            
            <div className="space-y-4">
              {SPOTIFY_PLAYLISTS.map((playlist) => (
                <div
                  key={playlist.id}
                  className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl opacity-60"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">{playlist.icon}</div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-gray-900">{playlist.name}</h4>
                      <p className="text-lg text-gray-600">{playlist.tracks} Songs</p>
                    </div>
                    <div className="text-gray-400">
                      <Play className="w-10 h-10" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== APPLE MUSIC TAB (PLACEHOLDER) ===== */}
      {activeTab === 'apple' && (
        <div className="space-y-6">
          {/* Info Card */}
          <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-3xl shadow-xl p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-5xl">🎶</div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Apple Music Integration</h2>
                <p className="text-lg text-gray-600">Musik-Streaming von Apple</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 mb-6">
              <p className="text-xl text-gray-700 leading-relaxed">
                Die Apple Music-Integration wird vorbereitet. Hier können später Ihre persönlichen Playlists, 
                Alben und Empfehlungen angezeigt werden.
              </p>
            </div>

            {/* Connect Button (Disabled Placeholder) */}
            <button
              onClick={handleAppleMusicConnect}
              className="w-full py-6 px-8 bg-gray-300 text-gray-600 rounded-2xl text-2xl font-bold cursor-not-allowed opacity-60 flex items-center justify-center gap-3"
              disabled
            >
              <div className="text-3xl">🔗</div>
              Mit Apple Music verbinden (Demnächst)
            </button>
          </div>

          {/* Example Playlists */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Empfohlene Playlists</h3>
            <p className="text-lg text-gray-600 mb-6">
              So könnten Ihre Playlists später aussehen:
            </p>
            
            <div className="space-y-4">
              {APPLE_PLAYLISTS.map((playlist) => (
                <div
                  key={playlist.id}
                  className="p-6 bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl opacity-60"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">{playlist.icon}</div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-gray-900">{playlist.name}</h4>
                      <p className="text-lg text-gray-600">{playlist.tracks} Songs</p>
                    </div>
                    <div className="text-gray-400">
                      <Play className="w-10 h-10" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
